import React, { FC, useEffect, useMemo, useState } from 'react';

import { BUTTON_TYPES, EvCSVExport, EvSection, EvTitle } from '@evinced-private/ui-common';

import {
	WFA_SESSION_ALL_ISSUES_TAB_DAID,
	WFA_SESSION_ISSUES_TABLE_DAID
} from 'data-automation-ids/WfaDashboardAutomationIds';
import { WfaSessionAllIssuesSkeleton } from 'src/apps/wfa/components/skeletons';
import { useWfaSessionAllIssuesQuery } from 'src/apps/wfa/hooks/useWfaSessionAllIssuesQuery';
import { useWfaSessionDetailsQuery } from 'src/apps/wfa/hooks/useWfaSessionDetailsQuery';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';
import { buildFullTableId } from 'src/common/helpers/TableStateHelper';
import { WFA_SESSION_ISSUES_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import { DevelopmentToggles } from 'src/common/services/TogglesService';

import { WfaSingleSessionFiltersSection } from '../../filters-section/WfaSingleSessionFiltersSection';

import { WFA_SESSION_ALL_ISSUES_CSV_FILE_NAME_PREFIX } from './WfaSessionAllIssues.constants';
import { LABELS, WFA_SESSION_ALL_ISSUES_CSV_HEADERS } from './WfaSessionAllIssues.labels';
import { getWfaAllIssuesColumns } from './WfaSessionAllIssuesTableHelper';

import './WfaSessionAllIssues.scss';

interface IWfaSessionIssuesProps {
	sessionId: string;
}

export const WfaSessionAllIssues: FC<IWfaSessionIssuesProps> = ({ sessionId }) => {
	const { getTableStateById, onFilterChange } = useTableState();
	const { configuration, getToggle } = useConfiguration();
	const isLabelsToggleEnabled = (configuration?.WFA?.TOGGLES as Record<string, boolean>)
		?.ENABLE_PLATFORM_LABELS as boolean;

	const tableId = useMemo(
		() => buildFullTableId(WFA_SESSION_ISSUES_TABLE_ID, sessionId),
		[sessionId]
	);
	const tableState = getTableStateById(tableId);
	const { filters, tableSort, paginationInfo } = tableState;

	const sessionDetailsQuery = useWfaSessionDetailsQuery(sessionId, isLabelsToggleEnabled);

	const issuesQuery = useWfaSessionAllIssuesQuery({
		sessionId,
		sort: tableSort?.[0],
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage,
		filters
	});

	const { total = 0, unfilteredTotal = 0, rows = [] } = issuesQuery.data ?? {};
	const isTableLoading = issuesQuery.isLoading || issuesQuery.isPreviousData;
	const isPageLoading =
		sessionDetailsQuery.isLoading || (!sessionDetailsQuery.data && isTableLoading);

	const [showInitialSkeleton, setShowInitialSkeleton] = useState(isPageLoading);

	const prepareCSVData = useMemo((): string[][] => {
		return rows.map((issue) => [
			issue.type,
			issue.severity,
			issue.components.join(', '),
			issue.selectors.join(', '),
			issue.url,
			issue.pageTitle
		]);
	}, [rows]);

	const csvExportProps = useMemo(
		() => ({
			triggerButtonProps: {
				type: BUTTON_TYPES.ACTION,
				children: LABELS.DOWNLOAD_CSV,
				title: LABELS.DOWNLOAD_CSV
			},
			fileName: `${WFA_SESSION_ALL_ISSUES_CSV_FILE_NAME_PREFIX}-${sessionId}`,
			headers: Object.values(WFA_SESSION_ALL_ISSUES_CSV_HEADERS),
			staticData: prepareCSVData
		}),
		[sessionId, prepareCSVData]
	);

	useEffect(() => {
		if (!isPageLoading && showInitialSkeleton) {
			setShowInitialSkeleton(false);
		}
	}, [isPageLoading, showInitialSkeleton]);

	const isCsvExportEnabled = useMemo(
		() => getToggle(DevelopmentToggles?.ENABLE_SESSION_ISSUES_CSV_EXPORT) ?? false,
		[getToggle]
	);

	if (showInitialSkeleton) {
		return <WfaSessionAllIssuesSkeleton isOverlay />;
	}

	return (
		<EvSection
			className="wfa-session-all-issues-view"
			dataAutomationId={WFA_SESSION_ALL_ISSUES_TAB_DAID}
		>
			<WfaSingleSessionFiltersSection
				sessionId={sessionId}
				filters={filters}
				ariaLabel="WFA Session's all issues filters"
				onFilterChange={onFilterChange}
				tableId={tableId}
			/>
			<EvSection ariaLabel="WFA Session's all issues table" skipLinkId="main-section">
				<div className="page-heading-container">
					<EvTitle titleText="Detected issues" headingLevel={2} className="page-heading" />

					{isCsvExportEnabled && prepareCSVData.length > 0 && <EvCSVExport {...csvExportProps} />}
				</div>
				{isTableLoading ? (
					<TableSkeleton columns={6} rowCount={5} />
				) : (
					<PlatformTable
						dataAutomationId={WFA_SESSION_ISSUES_TABLE_DAID}
						isTableLoading={isTableLoading}
						tableId={tableId}
						tableData={rows}
						totalCount={unfilteredTotal}
						totalPaginationableResults={total}
						className="session-issues-table"
						persistTableState
						columns={getWfaAllIssuesColumns()}
						options={{
							keyField: 'id',
							title: 'WFA Session Issues',
							caption: 'WFA Session Issues table',
							pagination: true,
							dataType: 'issues',
							remote: true
						}}
						withUrlSwitcher={true}
					/>
				)}
			</EvSection>
		</EvSection>
	);
};
